import { BRAND } from '@/lib/cms/config';

export const REPAIR_REQUEST_SUBMIT_STORAGE_KEY = `${BRAND}.repair_request.submit`;
export const REPAIR_REQUEST_SUBMIT_STORAGE_VERSION = 1;

export interface RepairRequestSubmitState {
  version: number;
  idempotencyKey: string;
  /** Stable JSON of the create-repair payload this submit record belongs to. */
  payloadFingerprint: string | null;
  repairNumber: string | null;
  newsletterDone: boolean;
  accountStepDone: boolean;
  accountRegisteredDuringFlow: boolean;
  watchStepDone: boolean;
  watchRegisteredDuringFlow: boolean;
}

export const REPAIR_REQUEST_SUBMIT_INITIAL_STATE: RepairRequestSubmitState = {
  version: REPAIR_REQUEST_SUBMIT_STORAGE_VERSION,
  idempotencyKey: '',
  payloadFingerprint: null,
  repairNumber: null,
  newsletterDone: false,
  accountStepDone: false,
  accountRegisteredDuringFlow: false,
  watchStepDone: false,
  watchRegisteredDuringFlow: false,
};

function readStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function loadRepairRequestSubmitState(): RepairRequestSubmitState {
  if (globalThis.window === undefined) {
    return { ...REPAIR_REQUEST_SUBMIT_INITIAL_STATE };
  }

  const raw = sessionStorage.getItem(REPAIR_REQUEST_SUBMIT_STORAGE_KEY);
  if (!raw) {
    return { ...REPAIR_REQUEST_SUBMIT_INITIAL_STATE };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<RepairRequestSubmitState>;
    if (parsed.version !== REPAIR_REQUEST_SUBMIT_STORAGE_VERSION) {
      return { ...REPAIR_REQUEST_SUBMIT_INITIAL_STATE };
    }

    return {
      version: REPAIR_REQUEST_SUBMIT_STORAGE_VERSION,
      idempotencyKey: typeof parsed.idempotencyKey === 'string' ? parsed.idempotencyKey : '',
      payloadFingerprint: readStringOrNull(parsed.payloadFingerprint),
      repairNumber: readStringOrNull(parsed.repairNumber),
      newsletterDone: readBoolean(parsed.newsletterDone),
      accountStepDone: readBoolean(parsed.accountStepDone),
      accountRegisteredDuringFlow: readBoolean(parsed.accountRegisteredDuringFlow),
      watchStepDone: readBoolean(parsed.watchStepDone),
      watchRegisteredDuringFlow: readBoolean(parsed.watchRegisteredDuringFlow),
    };
  } catch {
    sessionStorage.removeItem(REPAIR_REQUEST_SUBMIT_STORAGE_KEY);
    return { ...REPAIR_REQUEST_SUBMIT_INITIAL_STATE };
  }
}

export function saveRepairRequestSubmitState(state: RepairRequestSubmitState): void {
  if (globalThis.window === undefined) return;
  sessionStorage.setItem(REPAIR_REQUEST_SUBMIT_STORAGE_KEY, JSON.stringify(state));
}

export function clearRepairRequestSubmitState(): void {
  if (globalThis.window === undefined) return;
  sessionStorage.removeItem(REPAIR_REQUEST_SUBMIT_STORAGE_KEY);
}

function submitStateMatchesFingerprint(
  state: RepairRequestSubmitState,
  payloadFingerprint: string
): boolean {
  return Boolean(state.payloadFingerprint) && state.payloadFingerprint === payloadFingerprint;
}

export function ensureRepairSubmitIdempotencyKey(payloadFingerprint?: string): string {
  let state = loadRepairRequestSubmitState();
  const hasProgress = Boolean(state.idempotencyKey || state.repairNumber);

  if (
    payloadFingerprint !== undefined &&
    hasProgress &&
    !submitStateMatchesFingerprint(state, payloadFingerprint)
  ) {
    clearRepairRequestSubmitState();
    state = loadRepairRequestSubmitState();
  }

  if (state.idempotencyKey) {
    return state.idempotencyKey;
  }

  const idempotencyKey = crypto.randomUUID();
  saveRepairRequestSubmitState({
    ...state,
    idempotencyKey,
    ...(payloadFingerprint !== undefined ? { payloadFingerprint } : {}),
  });
  return idempotencyKey;
}

export function updateRepairRequestSubmitState(
  patch: Partial<Omit<RepairRequestSubmitState, 'version'>>
): RepairRequestSubmitState {
  const next = {
    ...loadRepairRequestSubmitState(),
    ...patch,
    version: REPAIR_REQUEST_SUBMIT_STORAGE_VERSION,
  };
  saveRepairRequestSubmitState(next);
  return next;
}
